import { clampToSrgb } from './gamut'
import { normalizeHexColor } from './normalize'
import { contrastRatio, hexToOklch, oklchToHex } from './oklch'

import type { Oklch } from './oklch'
import type { HexColor } from './types'

/**
 * Resolução dos tokens de marca a partir da cor do evento.
 *
 * Duas camadas dividem esse trabalho, e só uma mora aqui. As derivações
 * estéticas — active, tints de pill, sombras, halos, gradiente do hero — vivem
 * em `color-mix(in oklab, …)` no `styles/tailwind.css`, sem JavaScript. Aqui
 * ficam apenas os tokens que carregam contrato de contraste, porque só eles
 * precisam de um número medido em vez de uma razão de mistura.
 *
 * `light` está nesta camada, e não no CSS, porque é a superfície mais escura da
 * família — é contra ela que `text` mede o piso. Se a razão de mistura vivesse
 * no CSS e a medição usasse uma cópia dela em TS, as duas fontes divergiriam em
 * silêncio e a garantia de contraste morreria sem sinal.
 */

/** Roxo da plataforma. Só entra quando não há cor de evento utilizável. */
export const BRAND_FALLBACK: HexColor = '#8b2df7'

/** Superfície de referência do produto (`ACCESSIBILITY.md`), não branco puro. */
export const REFERENCE_SURFACE: HexColor = '#F9F7FF'

/** Piso WCAG para texto. */
export const TEXT_CONTRAST_FLOOR = 4.6

/** Piso WCAG para texto sobre preenchimento de marca. */
export const FILL_CONTRAST_FLOOR = 4.5

/**
 * Teto de deslocamento de lightness na normalização do preenchimento.
 *
 * O número saiu da base, não de gosto: o pior caso entre os eventos
 * gamificados vivos é `#00aaff` com 0.172, e o primeiro caso a rejeitar é
 * `#eab308` com 0.226. 0.20 cai no vão vazio entre os dois.
 */
export const MAX_LIGHTNESS_SHIFT = 0.2

/**
 * Proporção da marca no tint claro.
 *
 * Precisa bater com o fallback de `--color-brand-light` no `tailwind.css`: o
 * valor injetado vence o fallback em runtime, então divergir faria o CSS
 * declarar um tom que nunca aparece — e o teste da folha estaria conferindo
 * um valor morto.
 */
const LIGHT_MIX = 0.16

export interface BrandTokens {
  /** Preenchimento de marca. A cor do evento, aprofundada se preciso. */
  fill: HexColor
  /** Superfície tintada da família. Mais escura das superfícies claras. */
  light: HexColor
  /** Tinta sobre o preenchimento, resolvida por medição. */
  inverse: HexColor
  /** A marca como texto sobre superfície clara. */
  text: HexColor
}

export type BrandRejection = 'formato-invalido' | 'deslocamento-excessivo'

export type BrandClassification =
  | { usable: true }
  | { usable: false; reason: BrandRejection }

const toHex = (cor: Oklch): HexColor => oklchToHex(clampToSrgb(cor))

/** Passo da busca em lightness. 0.001 é ~1/4 de degrau de 8 bits. */
const PASSO_L = 0.001

/**
 * Menor escurecimento no próprio matiz que faz `alvo` alcançar o piso contra
 * a cor resultante. Devolve `null` quando nem o preto do matiz resolve.
 *
 * Escurecer é busca linear e não binária de propósito: contraste é monotônico
 * em L, mas queremos o **primeiro** tom que atinge o piso, não qualquer um —
 * parar no primeiro é o que mantém o deslocamento mínimo, e é o deslocamento
 * mínimo que preserva a cor escolhida pelo produtor.
 */
const escurecerAte = (
  base: Oklch,
  atingiu: (tom: HexColor) => boolean
): HexColor | null => {
  for (let l = base.l; l >= 0; l -= PASSO_L) {
    const tom = toHex({ ...base, l })
    if (atingiu(tom)) return tom
  }
  return null
}

const misturarComBranco = (base: Oklch, proporcao: number): HexColor => {
  // Mistura em OKLab — o mesmo espaço do `color-mix(in oklab)` que o CSS usa
  // nas derivações estéticas, para que as duas camadas não se contradigam.
  const branco = hexToOklch('#ffffff')
  return toHex({
    l: branco.l + (base.l - branco.l) * proporcao,
    c: base.c * proporcao,
    h: base.h
  })
}

const resolverFill = (base: Oklch): HexColor | null => {
  const literal = toHex(base)

  // Cor que já sustenta o piso não é deslocada: fidelidade antes de tudo.
  if (contrastRatio(literal, '#ffffff') >= FILL_CONTRAST_FLOOR) return literal

  return escurecerAte(
    base,
    tom => contrastRatio(tom, '#ffffff') >= FILL_CONTRAST_FLOOR
  )
}

/**
 * Tinta sobre o preenchimento de marca.
 *
 * Continua sendo MEDIÇÃO e não constante, embora hoje sempre responda branco:
 * `resolverFill` aprofunda o tom justamente até branco alcançar o piso, então
 * o primeiro ramo vence sempre. O segundo existe como rede — se a regra de
 * preenchimento mudar (voltar a aceitar a cor literal, por exemplo), é ele que
 * impede a tinta de virar ilegível em silêncio.
 *
 * O que NÃO existe mais é o ramo intermediário de "tinta escura tintada do
 * matiz". Ele fazia sentido na alternativa descartada, em que a cor entrava
 * literal e a tinta se virava; com o preenchimento normalizado ele era código
 * morto que documentava um comportamento inexistente.
 */
const resolverInverse = (fill: HexColor): HexColor =>
  contrastRatio(fill, '#ffffff') >= FILL_CONTRAST_FLOOR ? '#ffffff' : '#000000'

/**
 * O tom mais claro do matiz que ainda atinge o piso contra a superfície mais
 * escura da família. Medir contra o pior caso é o que permite um único token
 * servir em todas as superfícies do sistema — `light`, `#F9F7FF` e branco.
 */
const resolverText = (base: Oklch, light: HexColor): HexColor =>
  escurecerAte(base, tom => contrastRatio(tom, light) >= TEXT_CONTRAST_FLOOR) ??
  '#000000'

const derivarDeBase = (base: Oklch): BrandTokens | null => {
  const fill = resolverFill(base)
  if (!fill) return null

  const fillOklch = hexToOklch(fill)
  const light = misturarComBranco(fillOklch, LIGHT_MIX)

  return {
    fill,
    light,
    inverse: resolverInverse(fill),
    text: resolverText(fillOklch, light)
  }
}

/**
 * Classifica a cor para a validação de cadastro. Não é o mesmo teste do
 * fallback: a gamification aceita qualquer cor normalizável, enquanto o
 * cadastro recusa a que precisaria viajar demais.
 */
export const classifyBrandColor = (
  raw: string | null | undefined
): BrandClassification => {
  const hex = normalizeHexColor(raw)
  if (!hex) return { usable: false, reason: 'formato-invalido' }

  const base = hexToOklch(hex)
  const fill = resolverFill(base)
  if (!fill) return { usable: false, reason: 'deslocamento-excessivo' }

  const deslocamento = base.l - hexToOklch(fill).l
  return deslocamento > MAX_LIGHTNESS_SHIFT
    ? { usable: false, reason: 'deslocamento-excessivo' }
    : { usable: true }
}

/**
 * Tokens de marca para uma cor de evento.
 *
 * O fallback cobre ausência de dado — cor nula, vazia ou malformada — e não
 * cor difícil. Cor normalizável sempre rende no matiz do evento, mesmo quando
 * o cadastro a recusaria hoje: 43% dos eventos gamificados vivos foram
 * cadastrados sob a regra antiga e não podem perder a identidade no deploy.
 */
export const deriveBrandTokens = (
  raw: string | null | undefined
): BrandTokens => {
  const hex = normalizeHexColor(raw) ?? BRAND_FALLBACK
  return (
    derivarDeBase(hexToOklch(hex)) ??
    (derivarDeBase(hexToOklch(BRAND_FALLBACK)) as BrandTokens)
  )
}
